import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Activity, Cpu, HardDrive, Database, Wifi, Server, RefreshCw, 
  ChevronRight, ArrowUpRight, ArrowDownRight, Layers, ShieldCheck, CheckCircle2, XCircle, Clock, Loader2, Power 
} from 'lucide-react';
import SharedSSHUnlock from './SharedSSHUnlock';

const MonitoringTab = ({
  tenant,
  isVisible,
  sshSessionId,
  socket,
  sshStatus,
  sshError,
  onConnect,
  onDisconnect,
  isLeftPanelOpen,
  onToggleLeftPanel
}) => {
  const [metrics, setMetrics] = useState({
    cpuPerc: 0,
    memUsedGB: 0,
    memTotalGB: 0,
    memPerc: 0,
    diskUsedGB: 0,
    diskTotalGB: 0,
    diskPerc: 0,
    load1: 0,
    load5: 0,
    load15: 0,
    netRxKbps: 0,
    netTxKbps: 0,
    uptime: '—',
    containers: []
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const [isManualLoading, setIsManualLoading] = useState(false);
  const prevNetRef = useRef({ rx: 0, tx: 0, time: Date.now() });

  const fetchMetrics = useCallback(() => {
    if (!socket || !sshSessionId || !tenant?.DbName) return;
    socket.emit('ssh:getMonitoringStats', { sessionId: sshSessionId, dbName: tenant.DbName });
  }, [socket, sshSessionId, tenant?.DbName]);

  const handleManualRefresh = useCallback(() => {
    if (!socket || !sshSessionId || !tenant?.DbName) return;
    setIsManualLoading(true);
    socket.emit('ssh:getMonitoringStats', { sessionId: sshSessionId, dbName: tenant.DbName });
  }, [socket, sshSessionId, tenant?.DbName]);

  useEffect(() => {
    if (!socket) return;

    const handleStatsResult = (data) => {
      setInitialLoading(false);
      setIsManualLoading(false);
      if (!data || !data.success) return;

      const now = Date.now();
      const dt = Math.max((now - prevNetRef.current.time) / 1000, 1);
      
      let rxRate = 0;
      let txRate = 0;

      if (prevNetRef.current.rx > 0 && data.netRx >= prevNetRef.current.rx) {
        rxRate = (data.netRx - prevNetRef.current.rx) / dt / 1024; // KB/s
      }
      if (prevNetRef.current.tx > 0 && data.netTx >= prevNetRef.current.tx) {
        txRate = (data.netTx - prevNetRef.current.tx) / dt / 1024; // KB/s
      }

      prevNetRef.current = { rx: data.netRx, tx: data.netTx, time: now };

      setMetrics({
        cpuPerc: data.cpuPerc || 0,
        cpuCores: data.cpuCores || 1,
        memUsedGB: data.memUsedGB || 0,
        memTotalGB: data.memTotalGB || 0,
        memPerc: data.memPerc || 0,
        diskUsedGB: data.diskUsedGB || 0,
        diskTotalGB: data.diskTotalGB || 0,
        diskPerc: data.diskPerc || 0,
        load1: data.load1 || 0,
        load5: data.load5 || 0,
        load15: data.load15 || 0,
        netRxKbps: rxRate,
        netTxKbps: txRate,
        uptime: data.uptime || '—',
        containers: data.containers || []
      });
    };

    socket.on('ssh:monitoringStatsResult', handleStatsResult);

    return () => {
      socket.off('ssh:monitoringStatsResult', handleStatsResult);
    };
  }, [socket]);

  // Pure Event-Driven WebSocket Stream over SSH (0ms client polling, zero setInterval)
  useEffect(() => {
    if (!isVisible || sshStatus !== 'connected' || !socket || !sshSessionId || !tenant?.DbName) return;

    socket.emit('ssh:startMonitoringStream', { sessionId: sshSessionId, dbName: tenant.DbName });

    return () => {
      socket.emit('ssh:stopMonitoringStream');
    };
  }, [isVisible, sshStatus, socket, sshSessionId, tenant?.DbName]);

  // Format Helper for ASCII Progress Bar
  const renderProgressBar = (perc) => {
    const totalBlocks = 12;
    const filledBlocks = Math.min(Math.round((perc / 100) * totalBlocks), totalBlocks);
    const emptyBlocks = totalBlocks - filledBlocks;
    return '█'.repeat(filledBlocks) + '░'.repeat(emptyBlocks);
  };

  // Format Speed Helper
  const formatSpeed = (kbps) => {
    if (kbps >= 1024) {
      return `${(kbps / 1024).toFixed(1)} MB/s`;
    }
    return `${kbps.toFixed(1)} KB/s`;
  };

  if (sshStatus !== 'connected') {
    return (
      <div className="flex-1 flex flex-col h-full bg-slate-50 overflow-hidden">
        <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
          <div className="flex items-center gap-3">
            {!isLeftPanelOpen && (
              <button onClick={onToggleLeftPanel} className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0">
                <ChevronRight size={18} />
              </button>
            )}
            <div className="p-2.5 bg-cyan-50 text-cyan-600 rounded-lg border border-cyan-100">
              <Activity size={20} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Monitoring Système Live</h3>
              <p className="text-xs text-slate-500 font-medium">Métriques CPU, RAM, Disque, Réseau & Conteneurs</p>
            </div>
          </div>
        </div>
        <div className="flex-1 p-6 flex items-center justify-center">
          <SharedSSHUnlock 
            tenant={tenant} 
            status={sshStatus} 
            errorMessage={sshError} 
            onConnect={onConnect} 
            title="Accès Métriques SSH"
            description="Déverrouillez l'accès SSH pour visualiser la consommation CPU, RAM, Disque et Santé Docker en temps réel."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* Top Fixed Header */}
      <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
        <div className="flex items-center gap-3">
          {!isLeftPanelOpen && (
            <button onClick={onToggleLeftPanel} className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0">
              <ChevronRight size={18} />
            </button>
          )}
          <div className="p-2.5 bg-cyan-50 text-cyan-600 rounded-lg border border-cyan-100">
            <Activity size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Monitoring Live VPS</h3>
              {initialLoading ? (
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1.5 font-mono">
                  <Loader2 size={12} className="animate-spin text-amber-600" /> Collecte initiale...
                </span>
              ) : (
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 flex items-center gap-1 font-mono">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live Stream
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 font-medium">Uptime: <span className="font-mono font-bold text-slate-700">{metrics.uptime}</span></p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isManualLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-all disabled:opacity-50"
          >
            <RefreshCw size={14} className={isManualLoading ? 'animate-spin text-cyan-600' : ''} />
            <span>Actualiser</span>
          </button>
          {sshStatus === 'connected' && (
            <button
              onClick={() => {
                if (onDisconnect) {
                  onDisconnect();
                } else if (tenant?.id) {
                  sessionStorage.removeItem(`ssh_pass_${tenant.id}`);
                  if (socket && sshSessionId) socket.emit('ssh:disconnect', { sessionId: sshSessionId });
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-all shadow-xs"
              title="Déconnecter le SSH"
            >
              <Power size={14} />
              <span>Déconnecter</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">

        {/* INITIAL SKELETON SCREEN WHILE WAITING FOR INITIAL METRICS */}
        {initialLoading ? (
          <div className="space-y-6 animate-pulse">
            <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-xl flex items-center gap-3 text-cyan-800 text-xs font-bold">
              <Loader2 size={18} className="animate-spin text-cyan-600 shrink-0" />
              <span>Chargement des métriques du VPS en cours... Récupération de l'état CPU, RAM et santé Docker.</span>
            </div>

            {/* 4 Skeleton Hero Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs h-36 flex flex-col justify-between">
                  <div className="flex justify-between items-center">
                    <div className="w-24 h-4 bg-slate-200 rounded" />
                    <div className="w-12 h-4 bg-slate-200 rounded-full" />
                  </div>
                  <div className="w-20 h-8 bg-slate-300 rounded my-2" />
                  <div className="w-full h-3 bg-slate-200 rounded" />
                </div>
              ))}
            </div>

            {/* Load Average Skeleton */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs h-24 flex items-center justify-between">
              <div className="w-48 h-6 bg-slate-200 rounded" />
              <div className="flex gap-3">
                <div className="w-16 h-10 bg-slate-200 rounded" />
                <div className="w-16 h-10 bg-slate-200 rounded" />
                <div className="w-16 h-10 bg-slate-200 rounded" />
              </div>
            </div>

            {/* Table Skeleton */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <div className="w-40 h-5 bg-slate-200 rounded mb-4" />
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-full h-8 bg-slate-100 rounded" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Hero Cards Grid: CPU, RAM, DISK, NETWORK */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              
              {/* CPU Card */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs relative overflow-hidden flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                      <Cpu size={18} />
                    </div>
                    <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">CPU Usage</span>
                  </div>
                  <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${
                    metrics.cpuPerc > 85 ? 'bg-rose-50 text-rose-700 border-rose-200' :
                    metrics.cpuPerc > 60 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-emerald-50 text-emerald-700 border-emerald-200'
                  }`}>
                    {metrics.cpuPerc > 85 ? 'Critique' : metrics.cpuPerc > 60 ? 'Élevé' : 'Normal'}
                  </span>
                </div>

                <div className="my-3">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-3xl font-black text-slate-900 font-mono tracking-tight">{metrics.cpuPerc.toFixed(1)}%</span>
                    <span className="text-xs font-mono text-slate-400">vCPU: {metrics.cpuCores || 1} Cores</span>
                  </div>
                  {/* Visual ASCII Bar */}
                  <div className="font-mono text-xs font-bold text-blue-600 bg-slate-900 p-2 rounded-lg tracking-widest overflow-hidden text-center select-none shadow-inner">
                    {renderProgressBar(metrics.cpuPerc)} {metrics.cpuPerc.toFixed(0)}%
                  </div>
                </div>

                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      metrics.cpuPerc > 85 ? 'bg-rose-500' : metrics.cpuPerc > 60 ? 'bg-amber-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(metrics.cpuPerc, 100)}%` }}
                  />
                </div>
              </div>

              {/* RAM Card */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs relative overflow-hidden flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                      <Server size={18} />
                    </div>
                    <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">RAM Usage</span>
                  </div>
                  <span className="text-xs font-bold font-mono text-slate-600">
                    {metrics.memUsedGB.toFixed(1)} GB / {metrics.memTotalGB.toFixed(1)} GB
                  </span>
                </div>

                <div className="my-3">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-3xl font-black text-slate-900 font-mono tracking-tight">{metrics.memPerc.toFixed(1)}%</span>
                    <span className="text-xs font-mono text-slate-400">Libre: {(metrics.memTotalGB - metrics.memUsedGB).toFixed(1)} GB</span>
                  </div>
                  {/* Visual ASCII Bar */}
                  <div className="font-mono text-xs font-bold text-purple-400 bg-slate-900 p-2 rounded-lg tracking-widest overflow-hidden text-center select-none shadow-inner">
                    {renderProgressBar(metrics.memPerc)} {metrics.memPerc.toFixed(0)}%
                  </div>
                </div>

                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1">
                  <div 
                    className="h-full bg-purple-500 transition-all duration-500"
                    style={{ width: `${Math.min(metrics.memPerc, 100)}%` }}
                  />
                </div>
              </div>

              {/* Disk Storage Card */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs relative overflow-hidden flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                      <HardDrive size={18} />
                    </div>
                    <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">Disque Storage</span>
                  </div>
                  <span className="text-xs font-bold font-mono text-slate-600">
                    {metrics.diskUsedGB.toFixed(1)} GB / {metrics.diskTotalGB.toFixed(1)} GB
                  </span>
                </div>

                <div className="my-3">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-3xl font-black text-slate-900 font-mono tracking-tight">{metrics.diskPerc.toFixed(1)}%</span>
                    <span className="text-xs font-mono text-slate-400">Libre: {(metrics.diskTotalGB - metrics.diskUsedGB).toFixed(1)} GB</span>
                  </div>
                  {/* Visual ASCII Bar */}
                  <div className="font-mono text-xs font-bold text-emerald-400 bg-slate-900 p-2 rounded-lg tracking-widest overflow-hidden text-center select-none shadow-inner">
                    {renderProgressBar(metrics.diskPerc)} {metrics.diskPerc.toFixed(0)}%
                  </div>
                </div>

                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${Math.min(metrics.diskPerc, 100)}%` }}
                  />
                </div>
              </div>

              {/* Network Speed Card */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs relative overflow-hidden flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                      <Wifi size={18} />
                    </div>
                    <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">Trafic Réseau</span>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200 uppercase">Live IO</span>
                </div>

                <div className="my-2 space-y-2">
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2">
                      <ArrowUpRight size={16} className="text-emerald-500" />
                      <span className="text-xs font-bold text-slate-600">Upload (↑)</span>
                    </div>
                    <span className="font-mono text-sm font-black text-slate-900">{formatSpeed(metrics.netTxKbps)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2">
                      <ArrowDownRight size={16} className="text-blue-500" />
                      <span className="text-xs font-bold text-slate-600">Download (↓)</span>
                    </div>
                    <span className="font-mono text-sm font-black text-slate-900">{formatSpeed(metrics.netRxKbps)}</span>
                  </div>
                </div>
              </div>

            </div>

            {/* Load Average Banner */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-100 text-slate-700 rounded-xl">
                  <Clock size={22} />
                </div>
                <div>
                  <h4 className="text-sm font-black text-slate-900 uppercase tracking-wider">System Load Average</h4>
                  <p className="text-xs text-slate-500 font-medium">Charge moyenne processeur calculée sur 1m, 5m et 15m</p>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="flex-1 md:flex-initial px-4 py-2 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <div className="text-[10px] font-black text-slate-400 uppercase">1 Min</div>
                  <div className="font-mono text-base font-black text-slate-900">{metrics.load1}</div>
                </div>
                <div className="flex-1 md:flex-initial px-4 py-2 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <div className="text-[10px] font-black text-slate-400 uppercase">5 Min</div>
                  <div className="font-mono text-base font-black text-slate-900">{metrics.load5}</div>
                </div>
                <div className="flex-1 md:flex-initial px-4 py-2 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <div className="text-[10px] font-black text-slate-400 uppercase">15 Min</div>
                  <div className="font-mono text-base font-black text-slate-900">{metrics.load15}</div>
                </div>
              </div>
            </div>

            {/* Docker Containers Health & Resource Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers size={18} className="text-cyan-600" />
                  <h4 className="text-sm font-black text-slate-900 uppercase tracking-wider">Docker Containers Health</h4>
                </div>
                <span className="text-xs font-bold text-slate-500 font-mono">
                  {metrics.containers.filter(c => c.running).length} / {metrics.containers.length} Actifs
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                      <th className="py-3 px-5">Conteneur</th>
                      <th className="py-3 px-5">Service</th>
                      <th className="py-3 px-5">Statut Docker</th>
                      <th className="py-3 px-5">CPU %</th>
                      <th className="py-3 px-5">Mémoire RAM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs font-medium">
                    {metrics.containers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-400">
                          Aucun conteneur trouvé sur ce VPS.
                        </td>
                      </tr>
                    ) : (
                      metrics.containers.map((c) => (
                        <tr key={c.name} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-5 font-mono font-bold text-slate-800">
                            {c.name}
                          </td>
                          <td className="py-3.5 px-5 font-bold text-slate-700">
                            {c.label}
                          </td>
                          <td className="py-3.5 px-5">
                            {c.running ? (
                              <span className="px-2.5 py-1 rounded text-[10px] font-black bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                                Running <CheckCircle2 size={12} className="text-emerald-500" />
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 rounded text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                Stopped <XCircle size={12} className="text-rose-500" />
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-5 font-mono font-bold text-slate-900">
                            {c.cpuPerc !== undefined ? `${c.cpuPerc.toFixed(1)}%` : '—'}
                          </td>
                          <td className="py-3.5 px-5 font-mono font-bold text-slate-900">
                            {c.memUsage !== undefined ? c.memUsage : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default React.memo(MonitoringTab);
